/** @jsxImportSource @emotion/react */
import { useState, useLayoutEffect, useRef } from "react";
import { keyframes } from "@emotion/react";
import { NavLink } from "react-router-dom";

// Entrada de la barra en desktop: aparece deslizándose desde debajo del
// cabezal. Se anima al entrar a la sección (el layout monta la barra una vez
// y persiste entre subrutas).
const desplegarDesdeElCabezal = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0.4;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Estado a nivel de módulo para saber si al montarse una barra ya había otra
// visible en la navegación (ver comentario en el componente)
let barrasMontadas = 0;
let ultimoDesmontajeConBarra = 0;

/**
 * Botonera de navegación de sección.
 * En desktop (>= 768px) es una barra horizontal de tercer nivel que cuelga
 * centrada debajo del cabezal, ocupa solo el ancho que necesita y muestra
 * ícono + nombre de cada botón. En mobile (< 768px) es una barra fija en el
 * borde inferior de la pantalla, solo con los íconos.
 *
 * Cada botón navega a una ruta mediante NavLink. Soporta un badge opcional
 * con número de pendientes, o con cualquier texto corto —un "!" para un
 * aviso— eligiendo el color con badgeColor.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.items - [{ icono, etiqueta, to, badge, end }]
 *   - icono: clase de PrimeIcons (ej. "pi pi-info-circle")
 *   - etiqueta: texto del botón en desktop (en mobile queda como aria-label)
 *   - to: ruta destino (pasada a NavLink)
 *   - badge: número/string a mostrar como badge (opcional)
 *   - badgeColor: "rojo" (por defecto) o cualquier color de App.css, por
 *     ejemplo "amarillo" para un aviso que no es un pendiente contable
 *   - end: boolean pasado a NavLink para matcheo exacto (opcional)
 */
export default function NavLateral({ items = [] }) {
  // La animación corre solo cuando la barra APARECE en la navegación: si la
  // pantalla anterior ya tenía barra (subrutas de la misma sección, o pasar
  // de un cliente a un proceso), no se repite hasta pasar por una sección
  // sin barra. Tiene que ser useLayoutEffect y no el inicializador del
  // useState: el render del componente nuevo corre ANTES que el cleanup del
  // viejo, pero los layout effects corren después, así que acá el registro
  // del desmontaje anterior ya está escrito. Corre antes del paint, así que
  // cuando anima lo hace desde el primer frame.
  const [animar, setAnimar] = useState(false);

  useLayoutEffect(() => {
    const habiaBarra =
      barrasMontadas > 0 || Date.now() - ultimoDesmontajeConBarra < 300;
    barrasMontadas++;
    if (!habiaBarra) {
      setAnimar(true);
    }
    return () => {
      barrasMontadas--;
      ultimoDesmontajeConBarra = Date.now();
    };
  }, []);

  // En mobile la barra queda fija abajo y taparía a los botones flotantes
  // (asistente, destacar). Publica su alto real en --alto-barra-inferior para
  // que se suban por encima; en desktop, o sin barra, vale 0.
  const barraRef = useRef(null);
  useLayoutEffect(() => {
    const barra = barraRef.current;
    if (!barra) return;
    const raiz = document.documentElement;
    const mobile = window.matchMedia("(max-width: 767px)");
    const actualizar = () =>
      raiz.style.setProperty(
        "--alto-barra-inferior",
        mobile.matches ? `${barra.offsetHeight}px` : "0px"
      );
    actualizar();
    const observador = new ResizeObserver(actualizar);
    observador.observe(barra);
    mobile.addEventListener("change", actualizar);
    return () => {
      observador.disconnect();
      mobile.removeEventListener("change", actualizar);
      raiz.style.removeProperty("--alto-barra-inferior");
    };
  }, [items.length]);

  if (!items.length) return null;

  return (
    <div
      css={{
        // Centra la barra; la sombra del cabezal (zIndex mayor) queda encima,
        // así la animación se ve saliendo desde abajo de él
        "@media (min-width: 768px)": {
          display: "flex",
          justifyContent: "center"
        }
      }}
    >
      <div
        ref={barraRef}
        role="navigation"
        aria-label="Navegación de sección"
        className="morfeo-nav-lateral"
        css={{
          display: "flex",

          // Desktop: barra de tercer nivel colgando del cabezal, del ancho
          // que necesite
          "@media (min-width: 768px)": {
            position: "relative",
            zIndex: 1,
            flexDirection: "row",
            gap: "0.25em",
            width: "fit-content",
            padding: "0.4em 0.6em",
            backgroundColor: "white",
            borderRadius: "0 0 8px 8px",
            border: "1px solid #e0e0e0",
            borderTop: "none",
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.12)",
            animation: animar
              ? `${desplegarDesdeElCabezal} 0.3s ease-out`
              : "none"
          },

          // Mobile: fila horizontal fija abajo
          "@media (max-width: 767px)": {
            position: "fixed",
            right: 0,
            left: 0,
            bottom: 0,
            zIndex: 100,
            flexDirection: "row",
            justifyContent: "space-around",
            alignItems: "center",
            gap: 0,
            padding: `0.5em 0 calc(0.5em + env(safe-area-inset-bottom, 0px))`,
            backgroundColor: "white",
            borderTop: "1px solid #e0e0e0",
            boxShadow: "0 -2px 12px rgba(0, 0, 0, 0.08)"
          }
        }}
      >
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className="morfeo-nav-lateral-item"
            aria-label={item.etiqueta}
            css={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              color: "var(--grisTextos, #555)",
              textDecoration: "none",
              transition: "background-color 0.15s, color 0.15s",
              "&:hover": {
                backgroundColor: "var(--surface-hover, #f4f4f4)",
                color: "var(--principal)"
              },
              "&.active": {
                backgroundColor: "var(--principal)",
                color: "white"
              },
              "& i": {
                fontSize: "1.15em"
              },

              // Desktop: ícono + nombre
              "@media (min-width: 768px)": {
                gap: "0.5em",
                padding: "0.45em 0.9em",
                fontSize: "0.9em",
                whiteSpace: "nowrap"
              },

              // Mobile: solo el ícono
              "@media (max-width: 767px)": {
                flex: 1,
                flexDirection: "column",
                width: "auto",
                height: "auto",
                padding: "0.35em 0.25em",
                gap: "0.15em"
              }
            }}
          >
            <i className={item.icono} />
            <span
              css={{
                "@media (max-width: 767px)": { display: "none" }
              }}
            >
              {item.etiqueta}
            </span>
            {item.badge != null && item.badge !== 0 && item.badge !== "0" && (
              <span
                css={{
                  position: "absolute",
                  top: "-0.5em",
                  right: "-0.5em",
                  minWidth: "1.9em",
                  height: "1.9em",
                  padding: "0 0.5em",
                  borderRadius: "999px",
                  backgroundColor: `var(--${item.badgeColor || "rojo"})`,
                  color: "white",
                  fontSize: "0.85em",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)"
                }}
              >
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
